// Skills & Plugins catalog API — proxied by the aibot backend to validx,
// which owns the single Agents Anywhere CapabilityAsset catalog. The same
// assets appear in the validx workspace Settings and here.

import axios from 'axios';
import { getApiBaseUrl } from './api';

export interface CapabilityCatalogItem {
  id: string;
  /** Who it is on for, when it has been turned on and listed in the marketplace. */
  audience?: { everyone: boolean; departments: string[] };
  kind: 'skill' | 'plugin' | 'template' | 'connector';
  slug: string;
  name: string;
  description: string;
  category: string | null;
  sourceType: string;
  sourceRepo: string | null;
  sourceFormat: string | null;
  origin: 'agents_anywhere' | 'workspace' | 'partner';
  authorName: string | null;
  visibility: 'PLATFORM' | 'WORKSPACE' | 'PUBLIC';
  requiresApps: string[];
  hasSkillMd: boolean;
  bundledSkillCount: number;
  installCount: number;
  version: string;
  ownedByWorkspace: boolean;
  /** Platform skill that is part of the product: on for every project's engine,
   *  not toggleable. Set by the builder's catalog (content.alwaysOn). */
  alwaysOn?: boolean;
  /** Still the untouched SKILL.md template: placeholder description, no steps. */
  blank?: boolean;
}

export interface DetectedImportItem {
  key: string;
  type: 'skill' | 'plugin';
  format: string;
  name: string;
  description: string;
  path: string;
  fileCount: number;
  blank?: boolean;
}

function base(clientId: string): string {
  // getApiBaseUrl() already includes the /api suffix.
  return `${getApiBaseUrl()}/dashboard/capabilities/${encodeURIComponent(clientId)}`;
}

export interface CapabilityCatalog {
  items: CapabilityCatalogItem[];
  /** Ids the caller has switched on for themselves ("Just me"). */
  enabledForMe: string[];
}

export async function fetchCapabilityCatalog(clientId: string): Promise<CapabilityCatalog> {
  const res = await axios.get(base(clientId));
  return {
    items: Array.isArray(res.data?.items) ? res.data.items : [],
    enabledForMe: Array.isArray(res.data?.enabled_for_me) ? res.data.enabled_for_me : [],
  };
}

export async function setCapabilityForMe(clientId: string, capabilityId: string, enabled: boolean): Promise<void> {
  await axios.post(`${base(clientId)}/mine`, { capabilityId, enabled });
}

export async function createCapability(
  clientId: string,
  payload: {
    kind: 'skill' | 'plugin';
    name: string;
    description: string;
    instructions: string;
    requiresApps: string[];
    /** Optional grouping, e.g. 'routine' | 'scout' for scout-created skills. */
    category?: string;
  },
): Promise<void> {
  await axios.post(base(clientId), { action: 'create', ...payload });
}

export async function mutateCapability(
  clientId: string,
  action: 'publish' | 'unpublish' | 'delete',
  capabilityId: string,
): Promise<void> {
  await axios.post(base(clientId), { action, capabilityId });
}

export interface TurnOnResult {
  enabled: number;
  pending: number;
  listed: Array<{ skill_id: string; department: string | null }>;
  already_everyone?: boolean;
}

export async function turnOnCapability(
  clientId: string,
  capabilityId: string,
  audience: 'everyone' | 'departments' | 'my_team',
  departments: string[],
): Promise<TurnOnResult> {
  const res = await axios.post(`${base(clientId)}/turn-on`, { capabilityId, audience, departments });
  return res.data;
}

export async function turnOffCapability(
  clientId: string,
  capabilityId: string,
  audience: 'everyone' | 'my_team',
): Promise<void> {
  await axios.post(`${base(clientId)}/turn-off`, { capabilityId, audience, departments: [] });
}

export async function inspectGitHubSource(clientId: string, source: string): Promise<DetectedImportItem[]> {
  const res = await axios.post(`${base(clientId)}/import`, { action: 'inspect', source });
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

export async function importFromGitHub(clientId: string, source: string, keys: string[]): Promise<void> {
  await axios.post(`${base(clientId)}/import`, { action: 'import', source, keys });
}

export function apiErrorMessage(error: unknown, fallback: string): string {
  const detail = (error as any)?.response?.data?.detail;
  if (typeof detail === 'string' && detail) {
    if (detail === 'builder_not_configured') return 'Builder integration is not configured yet.';
    return detail;
  }
  return (error as any)?.message || fallback;
}

// ── Details, download, push ───────────────────────────────────────────────

export interface CapabilityPush {
  provider: 'github' | 'gitlab' | 'managed';
  repoFullName: string;
  path: string;
  branch: string;
  url: string;
  at: string;
  by: string | null;
}

export interface CapabilityDetails {
  id: string;
  kind: string;
  slug: string;
  name: string;
  description: string;
  category: string | null;
  version: string;
  visibility: 'PLATFORM' | 'WORKSPACE' | 'PUBLIC';
  ownedByWorkspace: boolean;
  sourceType: string;
  sourceRepo: string | null;
  sourcePath: string | null;
  sourceFormat: string | null;
  authorName: string | null;
  ownerEmail: string | null;
  companyOn: boolean;
  requiresApps: string[];
  createdAt: string;
  updatedAt: string;
  skillMd: string;
  files: { path: string; content: string }[];
  push: CapabilityPush | null;
}

export interface PushTargets {
  github: { connected: boolean; accounts: { installationId: string; accountLogin: string }[] };
  gitlab: { connected: boolean; connections: { id: string; label: string }[] };
  managed: { org: string; available: boolean };
}

export interface PushRepo {
  provider: 'github' | 'gitlab';
  fullName: string;
  defaultBranch: string;
  private: boolean;
  url: string;
  connectionId?: string | null;
}

export async function fetchCapabilityDetails(clientId: string, capabilityId: string): Promise<CapabilityDetails> {
  const res = await axios.get<CapabilityDetails>(`${base(clientId)}/${encodeURIComponent(capabilityId)}/details`);
  return res.data;
}

/** Fetches the zip with the signed-in session and hands it to the browser as a file. */
export async function downloadCapability(clientId: string, capabilityId: string, slug: string): Promise<void> {
  const res = await axios.get<Blob>(`${base(clientId)}/${encodeURIComponent(capabilityId)}/download`, { responseType: 'blob' });
  const href = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = href;
  a.download = `${slug || 'skill'}.skill.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

export async function fetchPushTargets(clientId: string): Promise<PushTargets> {
  const res = await axios.get<PushTargets>(`${base(clientId)}/push-targets`);
  return res.data;
}

export async function fetchPushRepos(clientId: string, provider: 'github' | 'gitlab', query = ''): Promise<PushRepo[]> {
  const res = await axios.get<{ repos: PushRepo[] }>(`${base(clientId)}/repos`, { params: { provider, q: query } });
  return res.data.repos || [];
}

export async function pushCapability(
  clientId: string,
  payload: { capabilityId: string; provider: 'github' | 'gitlab' | 'managed'; repoFullName?: string; path?: string; connectionId?: string | null },
): Promise<CapabilityPush> {
  const res = await axios.post<{ push: CapabilityPush }>(base(clientId), { action: 'push', ...payload });
  return res.data.push;
}

// ── The library: Agents Anywhere's, the company's, and your own ───────────

export type LibraryProvidedBy = 'aa' | 'company' | 'mine';

export type LibraryChange = 'share' | 'unshare' | 'self_on' | 'self_off' | 'remove';

export interface LibraryItem extends CapabilityCatalogItem {
  ownerEmail: string | null;
  companyOn: boolean;
  /** The owner's own switch: whether it rides their sessions. */
  ownerOn: boolean;
  /** Whether it rides this person's sessions: their own by the owner's switch,
   *  Agents Anywhere's once switched on, the company's unless switched off. */
  onForMe: boolean;
  /** The person who shared it, from the roster. Empty when unknown. */
  ownerName: string;
  ownerRole: string;
  /** The importer, the Owner, an AI Champion or the importer's department head may take it out of Company. */
  canChange: boolean;
  /** Only the person who imported it removes it. */
  canRemove?: boolean;
  providedBy: LibraryProvidedBy;
  /** This person's own, shared or not. */
  isMine: boolean;
}

export interface LibraryView {
  aa: LibraryItem[];
  company: LibraryItem[];
  mine: LibraryItem[];
  can_manage: boolean;
}

export async function fetchCapabilityLibrary(clientId: string): Promise<LibraryView> {
  const res = await axios.get(`${base(clientId)}/library`);
  return {
    aa: Array.isArray(res.data?.aa) ? res.data.aa : [],
    company: Array.isArray(res.data?.company) ? res.data.company : [],
    mine: Array.isArray(res.data?.mine) ? res.data.mine : [],
    can_manage: !!res.data?.can_manage,
  };
}

export async function inspectForLibrary(clientId: string, source: string): Promise<DetectedImportItem[]> {
  const res = await axios.post(`${base(clientId)}/library/import`, { action: 'inspect', source });
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

export async function importIntoLibrary(clientId: string, source: string, keys: string[]): Promise<void> {
  await axios.post(`${base(clientId)}/library/import`, { action: 'import', source, keys });
}

export async function changeLibraryItem(clientId: string, capabilityId: string, change: LibraryChange): Promise<void> {
  await axios.post(`${base(clientId)}/library/action`, { capabilityId, change });
}
